import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  MapPin, Calendar, Search, Heart, ArrowRight,
  SlidersHorizontal, X, SearchX,
} from 'lucide-react';
import api from '../../../Api.js';
import './HoneymoonPackages.css';

// ─── API ────────────────────────────────────────────────────────────
const API_TOKEN = '50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5';

const getPackages = async (params = {}) => {
  const res = await api.get('/honeymoon/packages', { params, headers: { 'X-API-TOKEN': API_TOKEN } });
  return res.data.data;
};

const getDestinations = async () => {
  const res = await api.get('/destinations', { headers: { 'X-API-TOKEN': API_TOKEN } });
  return res.data.data; // [{ id, name, cities: [{ id, name }] }]
};

// ─── Sub‑Components ─────────────────────────────────────────────────

const PackageSearchBar = ({ location, setLocation, date, setDate, onSearch }) => {
  const handleSubmit = e => { e.preventDefault(); onSearch(location, date); };
  return (
    <form className="package-search-bar" onSubmit={handleSubmit}>
      <div className="search-field">
        <MapPin size={20} className="field-icon" />
        <input type="text" placeholder="Where do you want to go?" value={location} onChange={e => setLocation(e.target.value)} />
      </div>
      <div className="search-field">
        <Calendar size={20} className="field-icon" />
        <input type="date" value={date} onChange={e => setDate(e.target.value)} />
      </div>
      <button type="submit" className="search-button"><Search size={18} /> Search Packages</button>
    </form>
  );
};

// ─── FilterSidebar with search ─────────────────────────────────────

const FilterSidebar = ({ destinations, selectedDest, selectedCity, onDestSelect, onCitySelect, onReset, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDestinations = useMemo(() => {
    if (!searchTerm.trim()) return destinations;
    const lower = searchTerm.toLowerCase();
    return destinations.filter(d => d.name.toLowerCase().includes(lower));
  }, [destinations, searchTerm]);

  const cities = useMemo(() => {
    const dest = destinations.find(d => d.id === selectedDest);
    if (!dest) return [];
    if (!searchTerm.trim()) return dest.cities;
    const lower = searchTerm.toLowerCase();
    return dest.cities.filter(c => c.name.toLowerCase().includes(lower));
  }, [destinations, selectedDest, searchTerm]);

  return (
    <div className="filter-sidebar">
      <div className="filter-header">
        <h3>Filters</h3>
        <button className="reset-link" onClick={onReset}>Reset All</button>
      </div>

      <div className="filter-search">
        <Search size={16} className="filter-search-icon" />
        <input
          type="text"
          placeholder="Search destinations or cities..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="filter-search-clear" onClick={() => setSearchTerm('')}>✕</button>
        )}
      </div>

      <div className="filter-group">
        <h4>Destination</h4>
        {loading ? (
          <p className="filter-loading">Loading...</p>
        ) : filteredDestinations.length === 0 ? (
          <p className="filter-empty">No matching destinations</p>
        ) : (
          <ul className="filter-list">
            {filteredDestinations.map(d => (
              <li key={d.id}>
                <label className={`filter-option ${selectedDest === d.id ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="destination"
                    checked={selectedDest === d.id}
                    onChange={() => onDestSelect(d.id)}
                  />
                  <span>{d.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedDest && (
        <div className="filter-group">
          <h4>City</h4>
          {cities.length === 0 ? (
            <p className="filter-empty">No cities match</p>
          ) : (
            <ul className="filter-list">
              {cities.map(c => (
                <li key={c.id}>
                  <label className={`filter-option ${selectedCity === c.id ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="city"
                      checked={selectedCity === c.id}
                      onChange={() => onCitySelect(c.id)}
                    />
                    <span>{c.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ─── MobileDrawer (reuses FilterSidebar) ──────────────────────────

const MobileFilterDrawer = ({ open, onClose, ...props }) => {
  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; return () => document.body.style.overflow = ''; }, [open]);
  return (
    <>
      <div className={`mobile-drawer-backdrop ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`mobile-drawer ${open ? 'open' : ''}`}>
        <div className="drawer-header"><h3>Filters</h3><button className="close-drawer" onClick={onClose}><X size={24} /></button></div>
        <div className="drawer-content"><FilterSidebar {...props} /></div>
        <div className="drawer-footer"><button className="apply-filters" onClick={onClose}>Apply Filters</button></div>
      </div>
    </>
  );
};

// ─── PackageCard, Skeleton, EmptyState (unchanged) ────────────────

const PackageCard = ({ package: pkg }) => {
  const fmt = n => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  return (
    <div className="package-card">
      <div className="card-image">
        <img src={pkg.featured_photo_url || 'https://via.placeholder.com/600x400?text=No+Image'} alt={pkg.name} loading="lazy" />
        <button className="wishlist-btn"><Heart size={20} /></button>
        <div className="card-badge1">{pkg.duration_days}D / {pkg.duration_nights}N</div>
      </div>
      <div className="card-body">
        <h3 className="card-title1">{pkg.name}</h3>
        {pkg.destination && <div className="card-destination"><MapPin size={16} /><span>{pkg.destination.name}</span></div>}
        <p className="card-description1">{pkg.short_description}</p>
        <div className="card-price"><span className="price-amount">Starting from {fmt(pkg.plans_starting_from)}</span><span className="price-label">per couple</span></div>
        <Link to={`/honeymoon/packages/${pkg.slug}`} className="card-button">View Package <ArrowRight size={18} /></Link>
      </div>
    </div>
  );
};

const PackageSkeleton = () => (
  <div className="package-skeleton">
    <div className="skeleton-image shimmer" /><div className="skeleton-body">
      <div className="skeleton-title shimmer" /><div className="skeleton-destination shimmer" />
      <div className="skeleton-description shimmer" /><div className="skeleton-price shimmer" /><div className="skeleton-button shimmer" />
    </div>
  </div>
);

const EmptyState = ({ onReset }) => (
  <div className="empty-state">
    <SearchX size={48} className="empty-icon" />
    <h3>No Honeymoon Packages Found</h3>
    <p>We couldn't find any romantic getaways matching your preferences. Try another destination or reset your filters.</p>
    <div className="empty-actions"><button className="reset-btn" onClick={onReset}>Clear Filters</button><button className="explore-btn" onClick={onReset}>Explore All Packages</button></div>
  </div>
);

// ─── Main Page ──────────────────────────────────────────────────────

const HoneymoonPackages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [selectedDest, setSelectedDest] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [searchLocation, setSearchLocation] = useState(searchParams.get('location') || '');
  const [travelDate, setTravelDate] = useState(searchParams.get('date') || '');
  const [sortOption, setSortOption] = useState('recommended');
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const sortPackages = useCallback((list, sort) => {
    const copy = [...list];
    switch (sort) {
      case 'price_low': return copy.sort((a, b) => a.plans_starting_from - b.plans_starting_from);
      case 'price_high': return copy.sort((a, b) => b.plans_starting_from - a.plans_starting_from);
      case 'duration_short': return copy.sort((a, b) => a.duration_days - b.duration_days);
      case 'duration_long': return copy.sort((a, b) => b.duration_days - a.duration_days);
      default: return copy;
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await getPackages(); setPackages(data); setFiltered(sortPackages(data, sortOption)); }
    catch (e) { console.error(e); setError('Unable to load honeymoon packages. Please try again.'); }
    finally { setLoading(false); }
  }, [sortPackages, sortOption]);

  const fetchByDest = useCallback(async (id) => {
    setFilterLoading(true); setError(null);
    try { const data = await getPackages({ destination_id: id }); setPackages(data); setFiltered(sortPackages(data, sortOption)); }
    catch (e) { console.error(e); setError('Unable to load packages for this destination.'); }
    finally { setFilterLoading(false); }
  }, [sortPackages, sortOption]);

  const fetchByCity = useCallback(async (id) => {
    setFilterLoading(true); setError(null);
    try { const data = await getPackages({ city_id: id }); setPackages(data); setFiltered(sortPackages(data, sortOption)); }
    catch (e) { console.error(e); setError('Unable to load packages for this city.'); }
    finally { setFilterLoading(false); }
  }, [sortPackages, sortOption]);

  const loadDestinations = useCallback(async () => {
    try { const data = await getDestinations(); setDestinations(data); }
    catch (e) { console.error(e); }
  }, []);

  // Initial load
  useEffect(() => {
    loadDestinations();
    const loc = searchParams.get('location');
    if (loc) {
      const fetchAndFilter = async () => {
        setLoading(true); setError(null);
        try {
          const data = await getPackages();
          setPackages(data);
          const lower = loc.toLowerCase();
          const filtered = data.filter(p =>
            p.name?.toLowerCase().includes(lower) ||
            p.destination?.name?.toLowerCase().includes(lower) ||
            p.short_description?.toLowerCase().includes(lower)
          );
          setFiltered(sortPackages(filtered, sortOption));
        } catch (e) { console.error(e); setError('Unable to load packages.'); }
        finally { setLoading(false); }
      };
      fetchAndFilter();
    } else {
      fetchAll();
    }
  }, []); // eslint-disable-line

  // Re-sort on sort change
  useEffect(() => {
    if (!loading && !filterLoading) setFiltered(prev => sortPackages(prev, sortOption));
  }, [sortOption, sortPackages, loading, filterLoading]);

  // Handlers
  const handleSearch = (loc, date) => {
    setSelectedDest(null); setSelectedCity(null);
    const params = new URLSearchParams();
    if (loc) params.set('location', loc);
    if (date) params.set('date', date);
    setSearchParams(params);
    if (!loc.trim()) { fetchAll(); return; }
    const perform = async () => {
      setLoading(true); setError(null);
      try {
        const data = await getPackages();
        setPackages(data);
        const lower = loc.toLowerCase().trim();
        const filtered = data.filter(p =>
          p.name?.toLowerCase().includes(lower) ||
          p.destination?.name?.toLowerCase().includes(lower) ||
          p.short_description?.toLowerCase().includes(lower)
        );
        setFiltered(sortPackages(filtered, sortOption));
      } catch (e) { console.error(e); setError('Search failed.'); }
      finally { setLoading(false); }
    };
    perform();
  };

  const handleDestSelect = (id) => {
    if (selectedDest === id) { setSelectedDest(null); setSelectedCity(null); fetchAll(); return; }
    setSelectedDest(id); setSelectedCity(null); fetchByDest(id);
  };

  const handleCitySelect = (id) => {
    if (selectedCity === id) { setSelectedCity(null); fetchAll(); return; }
    setSelectedCity(id); setSelectedDest(null); fetchByCity(id);
  };

  const handleReset = () => {
    setSelectedDest(null); setSelectedCity(null); setSearchLocation(''); setTravelDate('');
    setSortOption('recommended'); setSearchParams({}); fetchAll(); setMobileOpen(false);
  };

  const isFilterActive = selectedDest || selectedCity || searchLocation;

  return (
    <div className="honeymoon-packages">
      <div className="breadcrumb"><span>Home</span> / <span>Honeymoon Packages</span></div>
      <header className="page-header"><h1>Honeymoon Packages</h1><p>Find your perfect romantic getaway</p></header>



      <div className="packages-layout">
        <aside className="filter-sidebar-desktop">
          <FilterSidebar
            destinations={destinations} selectedDest={selectedDest} selectedCity={selectedCity}
            onDestSelect={handleDestSelect} onCitySelect={handleCitySelect} onReset={handleReset}
            loading={loading || filterLoading}
          />
        </aside>

        <section className="packages-content">
          <PackageSearchBar location={searchLocation} setLocation={setSearchLocation} date={travelDate} setDate={setTravelDate} onSearch={handleSearch} />
          <div className="results-header">
            <div className="results-count">
              {loading ? 'Loading packages...' : (
                <>Showing <strong>{filtered.length}</strong> Honeymoon Packages
                {searchLocation && <span className="search-query"> for "{searchLocation}"</span>}
                {isFilterActive && !searchLocation && <span className="filter-badge"> (filtered)</span>}</>
              )}
            </div>
            <div className="sort-controls">
              <label htmlFor="sort-select">Sort by</label>
              <select id="sort-select" value={sortOption} onChange={e => setSortOption(e.target.value)}>
                <option value="recommended">Recommended</option><option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option><option value="duration_short">Duration: Shortest</option>
                <option value="duration_long">Duration: Longest</option>
              </select>
            </div>
          </div>

          <button className="mobile-filter-toggle" onClick={() => setMobileOpen(true)}><SlidersHorizontal size={18} /> Filters</button>

          {error && (
            <div className="error-state"><p>{error}</p><button onClick={fetchAll} className="retry-button">Try Again</button></div>
          )}

          {!error && (
            <>
              {loading ? (
                <div className="packages-grid">{Array.from({ length: 6 }).map((_, i) => <PackageSkeleton key={i} />)}</div>
              ) : filtered.length === 0 ? (
                <EmptyState onReset={handleReset} />
              ) : (
                <div className="packages-grid">{filtered.map(p => <PackageCard key={p.id} package={p} />)}</div>
              )}
            </>
          )}
        </section>
      </div>

      <MobileFilterDrawer
        open={mobileOpen} onClose={() => setMobileOpen(false)}
        destinations={destinations} selectedDest={selectedDest} selectedCity={selectedCity}
        onDestSelect={handleDestSelect} onCitySelect={handleCitySelect} onReset={handleReset}
        loading={loading || filterLoading}
      />
    </div>
  );
};

export default HoneymoonPackages;